<template>
  <v-dialog v-model="dialog" max-width="800" persistent>
    <v-card class="modal-card">
      <v-card-title>
        {{ $vuetify.lang.t("$vuetify.gpt_dataset.configure_support") }}
      </v-card-title>

      <v-card-text class="p-4 modal-content">
        <v-row>
          <v-col cols="12" md="6" lg="3" class="d-flex align-center">
            <v-checkbox
              v-model="isOcrEnabled"
              color="primary"
              hide-details
              :disabled="isImageFile"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.enable_ocr')"
              @change="handleOcrChange"
            ></v-checkbox>
            <v-tooltip bottom max-width="350">
              <template #activator="{ on }">
                <span v-on="on" class="info-icon ml-6 mt-1" style="cursor: pointer">i</span>
              </template>
              <span>{{ $vuetify.lang.t("$vuetify.gpt_dataset.ocr_tooltip") }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="isTextExtractionFile" cols="12" md="6" class="d-flex align-center">
            <v-checkbox
              v-model="isTextExtractionEnabled"
              color="primary"
              hide-details
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.enable_text_extraction')"
              @change="handleTextExtractionChange"
            ></v-checkbox>
            <v-tooltip bottom max-width="350">
              <template #activator="{ on }">
                <span v-on="on" class="info-icon ml-6 mt-1" style="cursor: pointer">i</span>
              </template>
              <span>{{ $vuetify.lang.t("$vuetify.gpt_dataset.text_extraction_tooltip") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row v-if="isOcrEnabled">
          <v-col cols="12" md="6">
            <v-select
              v-model="specialMetadata.language"
              :items="languageOptions"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.language')"
              outlined
              clearable
            >
              <template #append>
                <v-tooltip right max-width="350">
                  <template #activator="{ on }">
                    <span v-on="on" class="info-icon">i</span>
                  </template>
                  <span>{{ $vuetify.lang.t("$vuetify.gpt_dataset.language_tooltip") }}</span>
                </v-tooltip>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="6" v-if="isOcrEnabled">
            <v-text-field
              v-model="specialMetadata.max_tokens"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.max_tokens')"
              type="number"
              min="1"
              :rules="[v => v === '' || v >= 1 || $vuetify.lang.t('$vuetify.gpt_dataset.max_tokens_validation')]"
              outlined
            >
              <template #append>
                <v-tooltip right max-width="350">
                  <template #activator="{ on }">
                    <span v-on="on" class="info-icon">i</span>
                  </template>
                  <span>{{ $vuetify.lang.t("$vuetify.gpt_dataset.max_tokens_tooltip") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="isOcrEnabled">
          <v-col cols="12">
            <v-textarea
              v-model="specialMetadata.instruction"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.instructions')"
              :placeholder="$vuetify.lang.t('$vuetify.gpt_dataset.instructions_placeholder')"
              outlined
              rows="3"
            >
              <template #append>
                <v-tooltip right max-width="350">
                  <template #activator="{ on }">
                    <span v-on="on" class="info-icon">i</span>
                  </template>
                  <span>{{ $vuetify.lang.t("$vuetify.gpt_dataset.instructions_tooltip") }}</span>
                </v-tooltip>
              </template>
            </v-textarea>
          </v-col>
        </v-row>
        <v-divider class="my-4"></v-divider>
        <v-row>
          <v-col cols="12">
            <div class="text-body-1 font-weight-medium primary--text mb-2">
              {{ $vuetify.lang.t("$vuetify.gpt_dataset.additional_metadata") }}
            </div>
          </v-col>
        </v-row>
        <v-row v-for="(item, index) in metadataItems" :key="index">
          <v-col cols="5">
            <v-text-field
              v-model="item.key"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.key')"
              outlined
              dense
              :error-messages="getFieldErrors(index, 'key')"
              @input="validateMetadataItem(index)"
              :placeholder="$vuetify.lang.t('$vuetify.gpt_dataset.key_placeholder')"
            />
          </v-col>

          <v-col cols="5">
            <v-text-field
              v-model="item.value"
              :label="$vuetify.lang.t('$vuetify.gpt_dataset.value')"
              outlined
              dense
              :error-messages="getFieldErrors(index, 'value')"
              @input="validateMetadataItem(index)"
              :placeholder="$vuetify.lang.t('$vuetify.gpt_dataset.value_placeholder')"
            />
          </v-col>
          <v-col>
            <v-btn icon @click="removeMetadataItem(index)" color="red lighten-2">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-btn @click="addMetadataItem" color="primary" class="mt-2" outlined>
          {{ $vuetify.lang.t("$vuetify.gpt_dataset.add_custom_field") }}
        </v-btn>

        <v-divider class="my-4"></v-divider>

        <div class="text-caption blue-grey--text">
          <v-icon small color="error">mdi-alert</v-icon>
          {{ $vuetify.lang.t("$vuetify.gpt_dataset.avoid_reserved_keys") }}
          <span class="red--text font-weight-medium">{{ reservedKeys.join(", ") }}</span>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4 modal-footer">
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog" class="mr-2 red--text">
          {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
        </v-btn>
        <v-btn color="primary" @click="saveMetadata" depressed :disabled="hasErrors">
          {{ $vuetify.lang.t("$vuetify.common.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";

export default {
  props: {
    value: Boolean,
    isImageFile: Boolean,
    isTextExtractionFile: Boolean,
    selectedFile: {
      type: [File, Object, Array, Boolean],
      default: null,
    },
    initialMetadata: {
      type: [Object, String],
      default: () => ({}),
    },
    initialIsOcr: Boolean,
    initialIsTextExtraction: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      dialog: this.value,
      metadataItems: [],
      metadataErrors: [],
      isOcrEnabled: this.initialIsOcr,
      isTextExtractionEnabled: this.initialIsTextExtraction,
      specialMetadata: {
        language: "",
        instruction: "",
        max_tokens: 2500,
      },
      languageOptions: [
        { text: "English", value: "en" },
        { text: "Japanese", value: "ja" },
        { text: "Korean", value: "ko" },
        { text: "Chinese", value: "zh" },
        { text: "Spanish", value: "es" },
        { text: "French", value: "fr" },
        { text: "German", value: "de" },
        { text: "Arabic", value: "ar" },
        { text: "Italian", value: "it" },
        { text: "Russian", value: "ru" },
      ],
    };
  },
  computed: {
    ...mapState({
      selectedDataset: state => state.project.selectedGptDataset,
    }),
    reservedKeys() {
      const baseKeys = ["content"];
      return this.selectedDataset?.indexColumnName ? [...baseKeys, this.selectedDataset.indexColumnName] : baseKeys;
    },
    hasErrors() {
      return this.metadataErrors.some(error => error.key.length > 0 || error.value.length > 0);
    },
    ocrDefault() {
      return this.isImageFile ? true : !!this.initialIsOcr;
    },
  },
  watch: {
    value(newVal) {
      this.dialog = newVal;
      if (newVal) {
        this.initializeToggleState();
        this.parseMetadata(this.initialMetadata || {});
      }
    },
    dialog(newVal) {
      this.$emit("input", newVal);
    },
    // data() reads this once, but the dialog outlives the file it was opened for.
    ocrDefault: {
      immediate: true,
      handler(val) {
        this.isOcrEnabled = val;
      },
    },
    selectedFile(newFile, previousFile) {
      if (newFile !== previousFile) {
        this.initializeToggleState();
        this.parseMetadata(this.initialMetadata || {});
      }
    },
    initialMetadata: {
      immediate: true,
      handler(metadata) {
        if (!this.dialog) this.parseMetadata(metadata || {});
      },
    },
  },
  methods: {
    initializeToggleState() {
      this.isOcrEnabled = this.isImageFile || this.initialIsOcr;
      this.isTextExtractionEnabled = this.initialIsTextExtraction;
      if (this.isTextExtractionFile && !this.isOcrEnabled && !this.isTextExtractionEnabled) {
        this.isTextExtractionEnabled = true;
      }
    },
    handleOcrChange(isEnabled) {
      if (this.isTextExtractionFile && !isEnabled && !this.isTextExtractionEnabled) {
        this.isTextExtractionEnabled = true;
      }
      if (!isEnabled) {
        this.specialMetadata.language = "";
        this.specialMetadata.instruction = "";
        this.specialMetadata.max_tokens = 2500;
      }
    },
    handleTextExtractionChange(isEnabled) {
      if (!isEnabled && !this.isOcrEnabled) {
        this.isOcrEnabled = true;
      }
    },

    parseMetadata(metadata) {
      try {
        const metaObj = typeof metadata === "string" ? JSON.parse(metadata) : metadata;
        this.specialMetadata = {
          language: metaObj.language || "",
          instruction: metaObj.instruction || "",
          max_tokens: metaObj.max_tokens || 2500,
        };
        this.metadataItems = Object.keys(metaObj)
          .filter(key => !["language", "instruction", "max_tokens"].includes(key))
          .map(key => ({ key, value: metaObj[key] }));

        if (!this.metadataItems.length) {
          this.addMetadataItem();
        }

        this.initMetadataErrors();
      } catch (e) {
        console.error("Error parsing metadata:", e);
        this.metadataItems = [];
        this.addMetadataItem();
      }
    },
    initMetadataErrors() {
      this.metadataErrors = this.metadataItems.map(() => ({ key: [], value: [] }));
      this.validateAllMetadata();
    },
    addMetadataItem() {
      this.metadataItems.push({ key: "", value: "" });
      this.metadataErrors.push({ key: [], value: [] });
    },
    removeMetadataItem(index) {
      this.metadataItems.splice(index, 1);
      this.metadataErrors.splice(index, 1);
      this.validateAllMetadata();
    },
    validateMetadataItem(index) {
      const errors = { key: [], value: [] };
      const { key, value } = this.metadataItems[index];

      if (value && !key) {
        errors.key.push(this.$vuetify.lang.t("$vuetify.gpt_dataset.key_required"));
      }
      if (key && this.reservedKeys.includes(key)) {
        errors.key.push(this.$vuetify.lang.t("$vuetify.gpt_dataset.reserved_key_error", [key]));
      }
      if (key && !value) {
        errors.value.push(this.$vuetify.lang.t("$vuetify.gpt_dataset.value_required"));
      }

      this.$set(this.metadataErrors, index, errors);
    },
    validateAllMetadata() {
      this.metadataItems.forEach((_, i) => this.validateMetadataItem(i));
      return !this.hasErrors;
    },
    getFieldErrors(index, field) {
      return this.metadataErrors[index]?.[field] || [];
    },
    closeDialog() {
      this.dialog = false;
    },
    saveMetadata() {
      this.validateAllMetadata();
      if (this.specialMetadata.max_tokens < 1 && this.specialMetadata.max_tokens !== "") return;
      if (this.hasErrors) return;

      let metadataObj = {};

      if (this.isOcrEnabled && this.specialMetadata.language) metadataObj.language = this.specialMetadata.language;
      if (this.isOcrEnabled && this.specialMetadata.instruction)
        metadataObj.instruction = this.specialMetadata.instruction;
      if (this.isOcrEnabled && this.specialMetadata.max_tokens !== "")
        metadataObj.max_tokens = this.specialMetadata.max_tokens;

      this.metadataItems.forEach(item => {
        if (item.key && item.value) metadataObj[item.key] = item.value;
      });

      this.$emit("save", {
        metadata: JSON.stringify(metadataObj),
        isOcr: this.isOcrEnabled,
        isTextExtraction: this.isTextExtractionEnabled,
      });

      this.closeDialog();
    },
  },
};
</script>

<style scoped>
.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
}

.modal-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.modal-content {
  overflow-y: auto;
  max-height: calc(90vh - 120px);
}

.modal-footer {
  border-top: 1px solid #eee;
}
</style>
